import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useDeleteVehicle,
  useDeleteVehicleDamage,
  useDeleteVehiclePhoto,
  useFollowUpVehicleDamage,
  useReportVehicleDamage,
  useSaveVehicle,
  useSetVehicleKm,
  useSetVehiclePhoto,
  useSetVehicleTyres,
  useStockLocations,
  useUsers,
  useVehicle,
  useVehicles
} from '../api/queries'
import type { Vehicle, VehicleDamage, VehicleDue, VehicleSaveInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { useCreateRequest } from '../lib/createRequest'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { formatDate, formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'
import { useModuleEnabled } from '../navigation'

/**
 * Firmabiler (docs/DECISIONS.md beslutning 123): hvem kører hvad, og hvornår skal bilen til syn, service og dækskift.
 * Varslerne sendes af serveren til kontoret og føreren. Alle kan melde en skade; kontoret opretter bilerne og følger op.
 */

const LEVEL_COLOR = { overdue: '#DC2626', soon: '#D97706', ok: '#64748B' } as const
const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)
const km = (value: number) => value.toLocaleString('da-DK')

function describeDue(d: VehicleDue) {
  if (d.kind === 'service_km') {
    if (d.kmLeft == null) return d.label
    return d.kmLeft <= 0 ? `Service - ${km(-d.kmLeft)} km over` : `Service om ${km(d.kmLeft)} km`
  }
  const days = d.daysUntil ?? 0
  const when =
    days < -1 ? `for ${-days} dage siden` : days === -1 ? 'i går' : days === 0 ? 'i dag' : days === 1 ? 'i morgen' : days <= 60 ? `om ${days} dage` : formatDate(d.date)
  return `${d.label} ${when}`
}

function worstLevel(v: Vehicle) {
  return v.due.some((d) => d.level === 'overdue') ? 'overdue' : v.due.some((d) => d.level === 'soon') ? 'soon' : 'ok'
}

export function VehiclesPage() {
  const { data, isLoading, error } = useVehicles()
  const { isIntranetAdmin } = useAuth()
  const [editing, setEditing] = useState<Vehicle | null | undefined>(undefined)
  const [reporting, setReporting] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  useCreateRequest(['bil', 'skade'], (kind) => {
    if (kind === 'bil' && isIntranetAdmin) setEditing(null)
    if (kind === 'skade') setReporting(true)
  })

  const shown = (data ?? []).filter((v) => showInactive || v.isActive)
  const overdue = shown.filter((v) => v.isActive && worstLevel(v) === 'overdue').length
  const soon = shown.filter((v) => v.isActive && worstLevel(v) === 'soon').length

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">{shown.length} biler</div>
        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Vis biler, der ikke er i brug
        </label>
        <button className="btn btn--sm" onClick={() => setReporting(true)}>🚗 Meld skade</button>
        {isIntranetAdmin && (
          <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>Opret bil</button>
        )}
      </div>

      {(overdue > 0 || soon > 0) && (
        <div className="banner" style={{ marginBottom: 12, borderLeft: `4px solid ${overdue > 0 ? LEVEL_COLOR.overdue : LEVEL_COLOR.soon}` }}>
          {overdue > 0 && <strong>{overdue} {overdue === 1 ? 'bil har' : 'biler har'} noget overskredet. </strong>}
          {soon > 0 && <span>{soon} {soon === 1 ? 'bil skal' : 'biler skal'} snart til syn, service eller dækskift.</span>}
        </div>
      )}

      {reporting && <DamageDialog vehicles={(data ?? []).filter((v) => v.isActive)} onClose={() => setReporting(false)} />}
      {editing !== undefined && <VehicleDialog vehicle={editing} onClose={() => setEditing(undefined)} />}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && shown.length === 0 && (
        <EmptyState>{isIntranetAdmin ? 'Ingen biler endnu. Tryk "Opret bil".' : 'Ingen biler endnu.'}</EmptyState>
      )}

      {shown.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 2 }}>
            {shown.map((v) => {
              const level = worstLevel(v)
              const pending = v.due.filter((d) => d.level !== 'ok')
              return (
                <Link key={v.id} to={`/vehicles/${v.id}`} className="device-row row" style={{ gap: 10, color: 'inherit', textDecoration: 'none', opacity: v.isActive ? 1 : 0.55 }}>
                  <span style={{ width: 6, alignSelf: 'stretch', borderRadius: 3, background: v.isActive && level !== 'ok' ? LEVEL_COLOR[level] : 'transparent' }} />
                  {/* Billedet står forrest, så bilen kan kendes på listen (Simon 2026-10-30). */}
                  {v.photoUrl ? <VehiclePhoto url={v.photoUrl} size={72} /> : <span className="vehicle-photo-empty" aria-hidden>🚗</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <Plate vehicle={v} />
                      {v.name && <span>{v.name}</span>}
                      {v.isMine && <span className="pill">Din bil</span>}
                      {!v.isActive && <span className="pill">Ikke i brug</span>}
                      {v.openDamages > 0 && <span className="pill" style={{ background: '#FEF3C7', color: '#92400E' }}>{v.openDamages} skade{v.openDamages === 1 ? '' : 'r'}</span>}
                    </div>
                    <div className="tiny muted">
                      {[v.make, v.model, v.year].filter(Boolean).join(' ') || 'Ukendt model'}
                      {v.driver ? ` · ${v.driver.displayName}` : ' · ingen fører'}
                      {v.currentKm != null ? ` · ${km(v.currentKm)} km` : ''}
                    </div>
                    {pending.length > 0 && (
                      <div className="tiny" style={{ marginTop: 2 }}>
                        {pending.map((d) => (
                          <span key={d.kind} style={{ color: LEVEL_COLOR[d.level], marginRight: 10 }}>{describeDue(d)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {v.driver && <Avatar user={v.driver} size={30} />}
                </Link>
              )
            })}
          </div>
        </Card>
      )}
    </>
  )
}

export function VehicleDetailPage() {
  const id = Number(useParams().id)
  const { data, isLoading, error } = useVehicle(id)
  const navigate = useNavigate()
  const remove = useDeleteVehicle()
  const [editing, setEditing] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return null

  const v = data.vehicle
  const canDrive = v.canEdit || v.isMine

  async function deleteVehicle() {
    if (!window.confirm(`Slet bilen ${v.displayPlate}?`)) return
    setFailed(null)
    try {
      await remove.mutateAsync(v.id)
      navigate('/vehicles')
    } catch (err) {
      setFailed(errorText(err, 'Bilen kunne ikke slettes.'))
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div><Link to="/vehicles" className="small">← Alle biler</Link></div>

      <Card title={`🚗 ${v.displayPlate}${v.name ? ` - ${v.name}` : ''}`}>
        <div className="stack" style={{ gap: 10 }}>
          {v.photoUrl && <VehiclePhoto url={v.photoUrl} size={0} />}
          <div className="small row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Plate vehicle={v} />
            {[v.make, v.model, v.year].filter(Boolean).join(' ') || 'Ukendt model'}
            {!v.isActive && <span className="pill">Ikke i brug</span>}
          </div>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            {v.driver ? (
              <>
                <Avatar user={v.driver} size={32} />
                <span className="small">Kører: <strong>{v.driver.displayName}</strong></span>
                {v.driver.phoneNumber && (
                  <a className="btn btn--sm" href={`tel:${v.driver.phoneNumber.replace(/\s/g, '')}`}>📞 {v.driver.phoneNumber}</a>
                )}
              </>
            ) : (
              <span className="small muted">Ingen fører</span>
            )}
          </div>
          {v.spareKeyNumber && <div className="small">🔑 Ekstranøgle: nr. <strong>{v.spareKeyNumber}</strong> i nøgleskabet</div>}
          {v.stockLocationId && <div className="small">Lager: <Link to={`/stock/locations/${v.stockLocationId}`}>{v.stockLocationName}</Link></div>}

          <div className="stack" style={{ gap: 4 }}>
            {v.due.length === 0 && <div className="small muted">Ingen datoer for syn, service eller forsikring.</div>}
            {v.due.map((d) => (
              <div key={d.kind} className="small" style={{ color: d.level === 'ok' ? undefined : LEVEL_COLOR[d.level] }}>
                {d.level === 'overdue' ? '⛔' : d.level === 'soon' ? '⚠' : '✓'} {describeDue(d)}
                {d.date && d.kind !== 'service_km' && <span className="muted"> ({formatDate(d.date)})</span>}
              </div>
            ))}
            <div className="tiny muted">
              {v.currentKm != null ? `${km(v.currentKm)} km` : 'Kilometertal ikke skrevet'}
              {v.kmUpdatedAt && ` · skrevet ${formatDate(v.kmUpdatedAt)}`}
              {v.tyreSeason && ` · ${TYRE_TEXT[v.tyreSeason]} på`}
              {v.leasingCompany && ` · leaset hos ${v.leasingCompany}`}
            </div>
          </div>

          {v.notes && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{v.notes}</div>}

          {canDrive && v.isActive && <DriverActions vehicle={v} />}

          <div className="btn-group">
            <button className="btn btn--sm btn--primary" onClick={() => setReporting(true)}>🚗 Meld skade</button>
            {v.canEdit && <button className="btn btn--sm" onClick={() => setEditing(true)}>Ret bilen</button>}
            {v.canEdit && <button className="btn btn--sm" disabled={remove.isPending} onClick={() => void deleteVehicle()}>Slet</button>}
          </div>
          {failed && <div className="error-box">{failed}</div>}
        </div>
      </Card>

      {reporting && <DamageDialog vehicles={[v]} onClose={() => setReporting(false)} />}
      {editing && <VehicleDialog vehicle={v} onClose={() => setEditing(false)} />}

      <Card title={`Skader (${data.damages.length})`}>
        {data.damages.length === 0 ? (
          <div className="small muted">Ingen skader meldt.</div>
        ) : (
          <div className="stack" style={{ gap: 12 }}>
            {data.damages.map((d) => <DamageRow key={d.id} damage={d} canFollowUp={v.canEdit} />)}
          </div>
        )}
      </Card>
    </div>
  )
}

/** Dækkene, som de står i teksten (helårsdæk fra Simon 2026-10-14). */
const TYRE_TEXT: Record<'summer' | 'winter' | 'all_season', string> = {
  summer: 'sommerdæk',
  winter: 'vinterdæk',
  all_season: 'helårsdæk'
}

/** Føreren og kontoret: kilometertal og dækskift. */
function DriverActions({ vehicle }: { vehicle: Vehicle }) {
  const setKm = useSetVehicleKm()
  const setTyres = useSetVehicleTyres()
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const other = vehicle.tyreSeason === 'winter' ? 'summer' : 'winter'

  async function saveKm() {
    setError(null)
    try {
      await setKm.mutateAsync({ id: vehicle.id, km: Number(value.replace(/\D/g, '')) })
      setValue('')
    } catch (err) {
      setError(errorText(err, 'Kilometertallet kunne ikke gemmes.'))
    }
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input inputMode="numeric" placeholder="Km-tæller nu" value={value} onChange={(e) => setValue(e.target.value)} style={{ maxWidth: 150 }} />
        <button className="btn btn--sm" disabled={!value.trim() || setKm.isPending} onClick={() => void saveKm()}>Gem km</button>
        {/* Helårsdæk skiftes ikke efter årstiden - de rettes under "Ret bilen". */}
        {vehicle.tyreSeason !== 'all_season' && (
          <button
            className="btn btn--sm"
            disabled={setTyres.isPending}
            onClick={() => setTyres.mutate({ id: vehicle.id, season: other }, { onError: (err) => setError(errorText(err, 'Dækskiftet kunne ikke gemmes.')) })}
          >
            {other === 'winter' ? '❄ Vinterdæk er på' : '☀ Sommerdæk er på'}
          </button>
        )}
      </div>
      {error && <div className="error-box">{error}</div>}
    </div>
  )
}

/** HC-Autos mærke (grønt "HC" i grøn ramme), tegnet efter deres logo - så fluebenet er til at kende. */
function HcAutoLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size * 2.1} height={size} viewBox="0 0 42 20" role="img" aria-label="HC-Auto" style={{ flexShrink: 0 }}>
      <rect x="1" y="1" width="40" height="18" rx="1.5" fill="#fff" stroke="#16A34A" strokeWidth="2" />
      <text x="21" y="15.5" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontStyle="italic" fontSize="15" fill="#16A34A">
        HC
      </text>
    </svg>
  )
}

/** Nummerpladen - gul eller hvid som den rigtige (beslutning 135). */
function Plate({ vehicle }: { vehicle: Vehicle }) {
  // Gul/hvid: bogstaverne på gult, tallene på hvidt - som på den rigtige plade.
  if (vehicle.plateColor === 'split') {
    const letters = /^[A-ZÆØÅ]+/i.exec(vehicle.displayPlate)?.[0] ?? ''
    return (
      <strong className="plate plate--split" title="Gul/hvid plade">
        <span className="plate__yellow">{letters}</span>
        {vehicle.displayPlate.slice(letters.length)}
      </strong>
    )
  }
  return <strong className={`plate ${vehicle.plateColor === 'yellow' ? 'plate--yellow' : ''}`}>{vehicle.displayPlate}</strong>
}

/** Længste side på billedet af bilen, når det lægges op. */
const VEHICLE_PHOTO_EDGE = 1280

/** Billedet af bilen (beslutning 135). size 0 = bilens side: højst 360 x 200, så det ikke fylder hele skærmen. */
function VehiclePhoto({ url, size }: { url: string; size: number }) {
  const blob = useAuthedBlob(url)
  const style = size
    ? { width: size, height: size * 0.75, objectFit: 'cover' as const, borderRadius: 6, flexShrink: 0 }
    : { width: '100%', maxWidth: 360, height: 200, objectFit: 'cover' as const, borderRadius: 8 }
  if (!blob) return <div style={{ ...style, background: '#F1F5F9' }} />
  return <img src={blob} alt="Billede af bilen" style={style} />
}

/** Vælg billeder - de lægges til, kan fjernes enkeltvis, og der kan tages flere efter hinanden med kameraet. */
function PhotoPickerField({ label, files, onChange, max = 6 }: { label: string; files: File[]; onChange: (files: File[]) => void; max?: number }) {
  return (
    <div className="field">
      <span className="field__label">{label} ({files.length}/{max})</span>
      {files.length > 0 && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {files.map((file, i) => (
            <span key={`${file.name}-${i}`} className="pill" style={{ gap: 4 }}>
              📷 {file.name.length > 18 ? `${file.name.slice(0, 15)}...` : file.name}
              <button type="button" className="link-button" aria-label="Fjern billedet" onClick={() => onChange(files.filter((_, j) => j !== i))}>✕</button>
            </span>
          ))}
        </div>
      )}
      {files.length < max && (
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            onChange([...files, ...Array.from(e.target.files ?? [])].slice(0, max))
            e.target.value = ''
          }}
        />
      )}
    </div>
  )
}

function DamagePhoto({ url }: { url: string }) {
  const blob = useAuthedBlob(url)
  if (!blob) return <div style={{ width: 88, height: 88, borderRadius: 6, background: '#F1F5F9' }} />
  return (
    <a href={blob} target="_blank" rel="noreferrer">
      <img src={blob} alt="Billede af skaden" style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 6 }} />
    </a>
  )
}

const STATUS_COLOR = { new: '#DC2626', ordered: '#D97706', fixed: '#16A34A' } as const

function DamageRow({ damage, canFollowUp }: { damage: VehicleDamage; canFollowUp: boolean }) {
  const followUp = useFollowUpVehicleDamage()
  const remove = useDeleteVehicleDamage()
  const [editing, setEditing] = useState(false)
  const [status, setStatus] = useState<string>(damage.status)
  const [text, setText] = useState(damage.followUp ?? '')
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setError(null)
    try {
      await followUp.mutateAsync({ vehicleId: damage.vehicleId, damageId: damage.id, status, followUp: text })
      setEditing(false)
    } catch (err) {
      setError(errorText(err, 'Opfølgningen kunne ikke gemmes.'))
    }
  }

  return (
    <div className="device-row stack" style={{ gap: 6 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <strong className="small">{damage.location ?? 'Skade'}</strong>
        <span className="pill" style={{ background: STATUS_COLOR[damage.status], color: '#fff' }}>{damage.statusName}</span>
      </div>
      <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{damage.description}</div>
      {damage.photoUrls.length > 0 && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {damage.photoUrls.map((url) => <DamagePhoto key={url} url={url} />)}
        </div>
      )}
      {(damage.hcAutoCalled || damage.roadsideAssistanceCalled || damage.policeCalled || damage.ambulanceCalled) && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {damage.hcAutoCalled && (
            <span className="pill" style={{ gap: 4 }}>
              <HcAutoLogo size={14} /> HC-Auto tilkaldt
            </span>
          )}
          {damage.roadsideAssistanceCalled && <span className="pill">🛻 Autohjælp tilkaldt</span>}
          {damage.policeCalled && <span className="pill">🚓 Politi tilkaldt</span>}
          {damage.ambulanceCalled && <span className="pill">🚑 Ambulance tilkaldt</span>}
        </div>
      )}
      {damage.hasCounterparty && (
        <div className="small" style={{ padding: 8, background: '#FFF7ED', borderRadius: 8 }}>
          <strong>Modpart</strong>
          {damage.counterparty ? (
            <>
              <div>
                {[
                  damage.counterparty.plate && `Nummerplade ${damage.counterparty.plate}`,
                  damage.counterparty.name,
                  damage.counterparty.insurance && `forsikret i ${damage.counterparty.insurance}`
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Ingen oplysninger skrevet'}
              </div>
              {damage.counterparty.phone && (
                <a className="btn btn--sm" style={{ marginTop: 4 }} href={`tel:${damage.counterparty.phone.replace(/\s/g, '')}`}>
                  📞 {damage.counterparty.phone}
                </a>
              )}
              {damage.counterparty.photoUrls.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {damage.counterparty.photoUrls.map((url) => <DamagePhoto key={url} url={url} />)}
                </div>
              )}
            </>
          ) : (
            <div className="tiny muted">Der var en anden bil involveret. Oplysningerne ser kontoret.</div>
          )}
        </div>
      )}
      <div className="tiny muted">
        Meldt af {damage.reportedBy?.displayName ?? 'en tidligere kollega'} · {formatDateTime(damage.reportedAt)}
        {damage.occurredOn && ` · skete ${formatDate(damage.occurredOn)}`}
      </div>
      {damage.followUp && !editing && (
        <div className="small" style={{ padding: 8, background: '#F8FAFC', borderRadius: 8 }}>
          <strong>Opfølgning:</strong> {damage.followUp}
          {damage.handledBy && <div className="tiny muted">{damage.handledBy.displayName}</div>}
        </div>
      )}
      {editing && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {(['new', 'ordered', 'fixed'] as const).map((s) => (
              <button key={s} type="button" className={`btn btn--sm ${status === s ? 'btn--primary' : ''}`} onClick={() => setStatus(s)}>
                {s === 'new' ? 'Ny' : s === 'ordered' ? 'Bestilt' : 'Udbedret'}
              </button>
            ))}
          </div>
          <textarea rows={2} maxLength={1000} placeholder="Hvad er gjort? F.eks. bestilt tid hos Autohuset 14/10" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="btn-group">
            <button className="btn btn--sm btn--primary" disabled={followUp.isPending} onClick={() => void save()}>Gem</button>
            <button className="btn btn--sm" onClick={() => setEditing(false)}>Annullér</button>
          </div>
        </div>
      )}
      {canFollowUp && !editing && (
        <div className="btn-group">
          <button className="btn btn--sm" onClick={() => setEditing(true)}>Følg op</button>
          <button className="btn btn--sm" disabled={remove.isPending} onClick={() => { if (window.confirm('Slet skaden?')) remove.mutate({ vehicleId: damage.vehicleId, damageId: damage.id }) }}>Slet</button>
        </div>
      )}
      {error && <div className="error-box">{error}</div>}
    </div>
  )
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal__card">{children}</div>
    </div>
  )
}

/** Meld en skade - med billeder. Bilen er forvalgt, når man selv kører en. */
function DamageDialog({ vehicles, onClose }: { vehicles: Vehicle[]; onClose: () => void }) {
  const report = useReportVehicleDamage()
  const [vehicleId, setVehicleId] = useState<number | ''>(() => (vehicles.length === 1 ? vehicles[0].id : vehicles.find((v) => v.isMine)?.id ?? ''))
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const [occurredOn, setOccurredOn] = useState('')
  const [files, setFiles] = useState<File[]>([])
  // Tilkaldt og modpart (beslutning 135).
  const [roadside, setRoadside] = useState(false)
  const [police, setPolice] = useState(false)
  const [ambulance, setAmbulance] = useState(false)
  const [hcAuto, setHcAuto] = useState(false)
  const [hasCounterparty, setHasCounterparty] = useState(false)
  const [other, setOther] = useState({ plate: '', name: '', phone: '', insurance: '' })
  const [otherFiles, setOtherFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function send() {
    if (vehicleId === '') return
    setError(null)
    try {
      const photos = []
      for (const file of files.slice(0, 6)) photos.push(await prepareDocumentFile(file))
      const otherPhotos = []
      if (hasCounterparty) for (const file of otherFiles.slice(0, 6)) otherPhotos.push(await prepareDocumentFile(file))
      await report.mutateAsync({
        vehicleId,
        description: description.trim(),
        location: location.trim(),
        occurredOn: occurredOn || null,
        photos,
        roadsideAssistanceCalled: roadside,
        policeCalled: police,
        ambulanceCalled: ambulance,
        hcAutoCalled: hcAuto,
        counterparty: hasCounterparty
          ? { plate: other.plate.trim(), name: other.name.trim(), phone: other.phone.trim(), insurance: other.insurance.trim(), photos: otherPhotos }
          : null
      })
      setDone(true)
    } catch (err) {
      setError(errorText(err, 'Skaden kunne ikke meldes.'))
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ marginTop: 0 }}>🚗 Meld skade på bil</h3>
      {done ? (
        <div className="stack" style={{ gap: 10 }}>
          <div className="map-notice">Tak - kontoret har fået besked og følger op.</div>
          <div><button className="btn btn--primary" onClick={onClose}>Luk</button></div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {vehicles.length > 1 && (
            <label className="field">
              <span className="field__label">Bil *</span>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Vælg bil ...</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{v.displayPlate}{v.name ? ` - ${v.name}` : ''}{v.isMine ? ' (din)' : ''}</option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span className="field__label">Hvor på bilen</span>
            <input maxLength={100} placeholder="F.eks. højre bagdør" value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Hvad er der sket? *</span>
            <textarea rows={3} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Hvornår skete det?</span>
            <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </label>
          <PhotoPickerField label="Billeder af skaden" files={files} onChange={setFiles} />

          <div className="field">
            <span className="field__label">Er der tilkaldt hjælp?</span>
            <label className="row small" style={{ gap: 8, alignItems: 'center' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={hcAuto} onChange={(e) => setHcAuto(e.target.checked)} />
              <HcAutoLogo /> HC-Auto er tilkaldt
            </label>
            <label className="row small" style={{ gap: 8 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={roadside} onChange={(e) => setRoadside(e.target.checked)} />
              🛻 Autohjælp er tilkaldt
            </label>
            <label className="row small" style={{ gap: 8 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={police} onChange={(e) => setPolice(e.target.checked)} />
              🚓 Politi er tilkaldt
            </label>
            <label className="row small" style={{ gap: 8 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={ambulance} onChange={(e) => setAmbulance(e.target.checked)} />
              🚑 Ambulance er tilkaldt
            </label>
          </div>

          <label className="row small" style={{ gap: 8 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={hasCounterparty} onChange={(e) => setHasCounterparty(e.target.checked)} />
            <strong>Der er en modpart (en anden bil)</strong>
          </label>
          {hasCounterparty && (
            <div className="stack" style={{ gap: 8, padding: 10, background: '#FFF7ED', borderRadius: 8 }}>
              <div className="form-grid">
                <label className="field">
                  <span className="field__label">Modpartens nummerplade</span>
                  <input maxLength={20} placeholder="AB 12 345" value={other.plate} onChange={(e) => setOther({ ...other, plate: e.target.value })} />
                </label>
                <label className="field">
                  <span className="field__label">Navn</span>
                  <input maxLength={100} value={other.name} onChange={(e) => setOther({ ...other, name: e.target.value })} />
                </label>
                <label className="field">
                  <span className="field__label">Telefon</span>
                  <input type="tel" maxLength={30} value={other.phone} onChange={(e) => setOther({ ...other, phone: e.target.value })} />
                </label>
                <label className="field">
                  <span className="field__label">Forsikringsselskab</span>
                  <input maxLength={100} value={other.insurance} onChange={(e) => setOther({ ...other, insurance: e.target.value })} />
                </label>
              </div>
              <PhotoPickerField label="Billeder af den anden bil" files={otherFiles} onChange={setOtherFiles} />
              <div className="tiny muted">Modpartens oplysninger og billeder ser kun kontoret og dig.</div>
            </div>
          )}
          {error && <div className="error-box">{error}</div>}
          <div className="btn-group">
            <button className="btn btn--primary" disabled={vehicleId === '' || !description.trim() || report.isPending} onClick={() => void send()}>
              {report.isPending ? 'Sender ...' : 'Meld skaden'}
            </button>
            <button className="btn" onClick={onClose}>Annullér</button>
          </div>
        </div>
      )}
    </Modal>
  )
}

/** Opret eller ret en bil - kontoret. */
function VehicleDialog({ vehicle, onClose }: { vehicle: Vehicle | null; onClose: () => void }) {
  const save = useSaveVehicle()
  const setPhoto = useSetVehiclePhoto()
  const removePhoto = useDeleteVehiclePhoto()
  // Billedet af bilen (beslutning 135) - lægges på, når bilen er gemt (en ny bil har intet id før).
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [dropPhoto, setDropPhoto] = useState(false)
  const { data: users } = useUsers()
  const stockOn = useModuleEnabled()('stock')
  const { data: locations } = useStockLocations({ kind: 'van' })
  const [form, setForm] = useState<VehicleSaveInput>(() => ({
    registrationNumber: vehicle?.displayPlate ?? '',
    plateColor: vehicle?.plateColor ?? 'yellow',
    spareKeyNumber: vehicle?.spareKeyNumber ?? '',
    name: vehicle?.name ?? '',
    make: vehicle?.make ?? '',
    model: vehicle?.model ?? '',
    year: vehicle?.year ?? null,
    driverUserId: vehicle?.driver?.id ?? null,
    stockLocationId: vehicle?.stockLocationId ?? null,
    nextInspectionOn: vehicle?.nextInspectionOn ?? null,
    nextServiceOn: vehicle?.nextServiceOn ?? null,
    nextServiceKm: vehicle?.nextServiceKm ?? null,
    currentKm: vehicle?.currentKm ?? null,
    tyreSeason: vehicle?.tyreSeason ?? null,
    insuranceExpiresOn: vehicle?.insuranceExpiresOn ?? null,
    leasingExpiresOn: vehicle?.leasingExpiresOn ?? null,
    leasingCompany: vehicle?.leasingCompany ?? '',
    notes: vehicle?.notes ?? '',
    isActive: vehicle?.isActive ?? true
  }))
  const [error, setError] = useState<string | null>(null)

  const set = <K extends keyof VehicleSaveInput>(key: K, value: VehicleSaveInput[K]) => setForm((f) => ({ ...f, [key]: value }))
  const num = (value: string) => (value.trim() === '' ? null : Number(value.replace(/\D/g, '')))
  const date = (key: 'nextInspectionOn' | 'nextServiceOn' | 'insuranceExpiresOn' | 'leasingExpiresOn', label: string) => (
    <label className="field">
      <span className="field__label">{label}</span>
      <input type="date" value={form[key] ?? ''} onChange={(e) => set(key, e.target.value || null)} />
    </label>
  )

  async function submit() {
    setError(null)
    try {
      const saved = await save.mutateAsync({ id: vehicle?.id, input: form })
      const id = saved.vehicle.id
      if (photoFile) {
        // Billedet af bilen skal bare kunne genkendes - 1280 px er rigeligt og holder det på 100-250 KB.
        await setPhoto.mutateAsync({ id, photo: await prepareDocumentFile(photoFile, VEHICLE_PHOTO_EDGE) })
      } else if (dropPhoto && vehicle?.photoUrl) {
        await removePhoto.mutateAsync(id)
      }
      onClose()
    } catch (err) {
      setError(errorText(err, 'Bilen kunne ikke gemmes.'))
    }
  }

  return (
    <Modal onClose={onClose}>
      <h3 style={{ marginTop: 0 }}>{vehicle ? `Ret ${vehicle.displayPlate}` : 'Opret bil'}</h3>
      <div className="stack" style={{ gap: 10 }}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Nummerplade *</span>
            <input value={form.registrationNumber} placeholder="AB 12 345" onChange={(e) => set('registrationNumber', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Pladens farve</span>
            <select value={form.plateColor} onChange={(e) => set('plateColor', e.target.value as VehicleSaveInput['plateColor'])}>
              <option value="yellow">Gul plade (varebil)</option>
              <option value="white">Hvid plade</option>
              <option value="split">Gul/hvid plade (bogstaver på gult)</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Navn</span>
            <input value={form.name ?? ''} placeholder="F.eks. Bil 3" onChange={(e) => set('name', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Mærke</span>
            <input value={form.make ?? ''} placeholder="F.eks. Ford" onChange={(e) => set('make', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Model</span>
            <input value={form.model ?? ''} placeholder="F.eks. Transit Custom" onChange={(e) => set('model', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Årgang</span>
            <input inputMode="numeric" value={form.year ?? ''} onChange={(e) => set('year', num(e.target.value))} />
          </label>
          <label className="field">
            <span className="field__label">Fører</span>
            <select value={form.driverUserId ?? ''} onChange={(e) => set('driverUserId', e.target.value ? Number(e.target.value) : null)}>
              <option value="">Ingen</option>
              {(users ?? []).filter((u) => u.isActive).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
            </select>
          </label>
          {stockOn && (
            <label className="field">
              <span className="field__label">Bilens lager</span>
              <select value={form.stockLocationId ?? ''} onChange={(e) => set('stockLocationId', e.target.value ? Number(e.target.value) : null)}>
                <option value="">Intet</option>
                {(locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
          )}
          {date('nextInspectionOn', 'Næste syn')}
          {date('nextServiceOn', 'Næste service (dato)')}
          <label className="field">
            <span className="field__label">Næste service (km)</span>
            <input inputMode="numeric" value={form.nextServiceKm ?? ''} onChange={(e) => set('nextServiceKm', num(e.target.value))} />
          </label>
          <label className="field">
            <span className="field__label">Km-tæller nu</span>
            <input inputMode="numeric" value={form.currentKm ?? ''} onChange={(e) => set('currentKm', num(e.target.value))} />
          </label>
          <label className="field">
            <span className="field__label">Dæk på nu</span>
            <select value={form.tyreSeason ?? ''} onChange={(e) => set('tyreSeason', (e.target.value || null) as VehicleSaveInput['tyreSeason'])}>
              <option value="">Ved ikke</option>
              <option value="summer">Sommerdæk</option>
              <option value="winter">Vinterdæk</option>
              <option value="all_season">Helårsdæk</option>
            </select>
          </label>
          {date('insuranceExpiresOn', 'Forsikring udløber')}
          {date('leasingExpiresOn', 'Leasing udløber')}
          <label className="field">
            <span className="field__label">Ekstranøgle nr. i nøgleskabet</span>
            <input maxLength={20} placeholder="F.eks. 12" value={form.spareKeyNumber ?? ''} onChange={(e) => set('spareKeyNumber', e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Leasingselskab</span>
            <input value={form.leasingCompany ?? ''} onChange={(e) => set('leasingCompany', e.target.value)} />
          </label>
        </div>
        <div className="field">
          <span className="field__label">Billede af bilen</span>
          {vehicle?.photoUrl && !photoFile && !dropPhoto && (
            <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <VehiclePhoto url={vehicle.photoUrl} size={96} />
              <button type="button" className="btn btn--sm" onClick={() => setDropPhoto(true)}>Fjern billedet</button>
            </div>
          )}
          {dropPhoto && <div className="tiny muted">Billedet fjernes, når du trykker "Gem".</div>}
          {photoFile && <div className="tiny">📷 {photoFile.name} - lægges på, når du trykker "Gem".</div>}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              setPhotoFile(e.target.files?.[0] ?? null)
              setDropPhoto(false)
            }}
          />
        </div>
        <label className="field">
          <span className="field__label">Noter</span>
          <textarea rows={2} maxLength={1000} value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
        </label>
        <label className="row small" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
          I brug (fjern fluebenet, når bilen er solgt eller afleveret)
        </label>
        <div className="tiny muted">Kontoret og føreren får besked 30 dage før syn, service, forsikring og leasing, og 3 uger før dækskift.</div>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={!form.registrationNumber.trim() || save.isPending || setPhoto.isPending} onClick={() => void submit()}>
            {save.isPending || setPhoto.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" onClick={onClose}>Annullér</button>
        </div>
      </div>
    </Modal>
  )
}
